import * as React from "react";
import { useProgressivePolling, Button, Badge, Progress } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

// `useProgressivePolling` is headless — it renders nothing on its own, it just
// drives a poll callback on a backoff schedule (1s, 2s, 3s, 5s, 8s, 10s, then
// 15s) and pauses when the tab is hidden. To make it visible here we wrap it in
// a small mock-job widget that escalates a fake "generation" from queued, to
// processing, to ready, and surfaces the observable state (the status, how many
// polls fired, the current step index, and the delay the next tick will use).

type JobStatus = "idle" | "processing" | "ready";

// A snappy schedule (still escalating) so the demo finishes in a few seconds
// instead of the production 1s→15s ramp. Shared with the delay read-out below.
const DEMO_SCHEDULE = [400, 700, 1100, 1600, 2200] as const;
const DEMO_FINAL_MS = 2800;

function stepDelayMs(index: number): number {
  return index < DEMO_SCHEDULE.length ? DEMO_SCHEDULE[index] : DEMO_FINAL_MS;
}

function MockJobWidget({ pollsUntilReady = 5 }: { pollsUntilReady?: number }) {
  const [status, setStatus] = React.useState<JobStatus>("idle");
  const [pollCount, setPollCount] = React.useState(0);

  const isActive = status === "processing";

  const { resetPollingSchedule, currentPollIndex } = useProgressivePolling({
    // Each tick advances the mock job; once it has polled enough times the job
    // reports "ready" and the terminal predicate below stops the timer.
    pollFn: () => {
      setPollCount((n) => {
        const next = n + 1;
        if (next >= pollsUntilReady) setStatus("ready");
        return next;
      });
    },
    isActive,
    shouldStopPolling: () => status === "ready",
    schedule: DEMO_SCHEDULE,
    finalIntervalMs: DEMO_FINAL_MS,
  });

  const start = () => {
    setPollCount(0);
    setStatus("processing");
    resetPollingSchedule();
  };

  const reset = () => {
    setPollCount(0);
    setStatus("idle");
    resetPollingSchedule();
  };

  const pct = Math.min(100, Math.round((pollCount / pollsUntilReady) * 100));
  const badge =
    status === "ready"
      ? { variant: "success" as const, label: "Ready" }
      : status === "processing"
        ? { variant: "warning" as const, label: "Processing" }
        : { variant: "secondary" as const, label: "Idle" };

  return (
    <div className="w-80 space-y-4 rounded-card border border-hairline bg-surface p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-ink">Generation job</span>
        <Badge variant={badge.variant}>{badge.label}</Badge>
      </div>

      <Progress value={pct} />

      <dl className="space-y-1.5 text-sm">
        <div className="flex items-center justify-between">
          <dt className="text-ink-2">Polls fired</dt>
          <dd className="tabular-nums font-medium text-ink">{pollCount}</dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-ink-2">Backoff step</dt>
          <dd className="tabular-nums font-medium text-ink">{currentPollIndex}</dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-ink-2">Next tick in</dt>
          <dd className="tabular-nums font-medium text-ink">
            {status === "processing" ? `${stepDelayMs(currentPollIndex)}ms` : "paused"}
          </dd>
        </div>
      </dl>

      <div className="flex gap-2">
        <Button size="sm" onClick={start} disabled={isActive}>
          {status === "ready" ? "Run again" : "Start job"}
        </Button>
        <Button size="sm" variant="outline" onClick={reset}>
          Reset
        </Button>
      </div>
    </div>
  );
}

// A job that never reports ready, capped by `maxDurationMs`. When the cap
// elapses the hook fires `onTimeout` once and pauses the timer, the pattern an
// upload-extraction screen uses to stop waiting on a stuck job.
function CappedJobWidget() {
  const [status, setStatus] = React.useState<JobStatus>("idle");
  const [pollCount, setPollCount] = React.useState(0);
  const [timedOut, setTimedOut] = React.useState(false);

  const isActive = status === "processing";

  const { currentPollIndex, resetPollingSchedule } = useProgressivePolling({
    pollFn: () => setPollCount((n) => n + 1),
    isActive,
    // The mock job intentionally never finishes, so only the cap stops it.
    shouldStopPolling: () => false,
    schedule: DEMO_SCHEDULE,
    finalIntervalMs: DEMO_FINAL_MS,
    maxDurationMs: 3000,
    onTimeout: () => {
      setTimedOut(true);
      setStatus("idle");
    },
  });

  const start = () => {
    setPollCount(0);
    setTimedOut(false);
    setStatus("processing");
    resetPollingSchedule();
  };

  return (
    <div className="w-80 space-y-4 rounded-card border border-hairline bg-surface p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-ink">Stuck job (3s cap)</span>
        <Badge variant={timedOut ? "destructive" : isActive ? "warning" : "secondary"}>
          {timedOut ? "Timed out" : isActive ? "Processing" : "Idle"}
        </Badge>
      </div>

      <dl className="space-y-1.5 text-sm">
        <div className="flex items-center justify-between">
          <dt className="text-ink-2">Polls fired</dt>
          <dd className="tabular-nums font-medium text-ink">{pollCount}</dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-ink-2">Backoff step</dt>
          <dd className="tabular-nums font-medium text-ink">{currentPollIndex}</dd>
        </div>
      </dl>

      {timedOut ? (
        <p className="text-sm text-ink-3">
          Cap reached before ready, onTimeout fired and polling paused.
        </p>
      ) : null}

      <Button size="sm" onClick={start} disabled={isActive}>
        {timedOut ? "Try again" : "Start job"}
      </Button>
    </div>
  );
}

export function ProgressivePollingDemo() {
  return (
    <Section
      id="progressive-polling"
      title="useProgressivePolling"
      description="Headless backoff polling, visibility-aware. Composes the library's own useInterval + useDocumentVisibility into a backoff state machine (1s, 2s, 3s, 5s, 8s, 10s, then 15s) that pauses when the tab is hidden and exposes resetPollingSchedule + currentPollIndex. It renders nothing itself, so each example wires it into a small live widget."
    >
      <VariantBlock
        label="Mock job to Ready"
        caption="Start it, watch the backoff step climb and the polls slow until the job reports ready and polling stops on its own"
      >
        <MockJobWidget />
      </VariantBlock>

      <VariantBlock
        label="Capped (onTimeout)"
        caption="A job that never finishes hits maxDurationMs, fires onTimeout once, and pauses"
      >
        <CappedJobWidget />
      </VariantBlock>
    </Section>
  );
}
